// GuildConsumablesTab.tsx
// Officer tool: paste a Warcraft Logs report URL, preview per-player consumable
// coverage (fights used / eligible boss fights per category), then commit to
// award heat. Coverage based: spreading usage across fights is what scores,
// dumping everything on one boss does not. Max per night equals attendance.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "./config";

import { tc } from "./theme/legible";
type MemberStub = { id: string; name: string };

type Props = {
  guildId: string;
  members: MemberStub[];
  // The game the dashboard is showing; its consumable list scores the logs.
  gameId?: string | null;
};

type ConfigCategory = {
  key: string;
  label: string;
  applies_to: string[];
  is_bonus: boolean;
  spells: { spell_id: number; label: string }[];
};

type PreviewCategory = {
  key: string;
  label: string;
  used: number;
  eligible: number;
  casts: number;
  is_bonus: boolean;
  applies: boolean | null;
  items?: string[];
};

type FightRef = { id: number; name: string; kill: boolean; zone_name?: string | null };

type DetailCategory = {
  key: string;
  label: string;
  is_bonus: boolean;
  used: number;
  eligible: number;
  points: number;
  items: string[];
  fights: FightRef[] | number[];
};

type RowDetail = {
  zones: { zone_id: number; zone_name: string | null; eligible: number; points: number }[];
  categories: DetailCategory[];
};

type PreviewRow = {
  actor_id: number;
  wcl_name: string;
  wcl_class: string | null;
  match_status: "matched" | "mapped" | "ambiguous" | "unmatched";
  character_id: string | null;
  character_name: string | null;
  role: string | null;
  match_candidates: { id: string; name: string }[];
  categories: PreviewCategory[];
  detail?: RowDetail | null;
  proposed_heat: number | null;
};

type PreviewReport = {
  code: string;
  title: string;
  zone: string | null;
  starts_at: string;
  fights_eligible: number;
  fights: FightRef[];
  zones?: { zone_id: number; zone_name: string | null; fights: number }[];
  max_heat?: number;
};

type ImportRow = {
  id: string;
  report_code: string;
  report_url: string;
  report_title: string | null;
  zone_name: string | null;
  report_start: string;
  fights_eligible: number;
  fights_total: number;
  imported_at: string;
  revoked_at: string | null;
  revoke_note: string | null;
  imported_by_name: string | null;
  revoked_by_name: string | null;
  players: number;
  total_heat: number;
};

type DetailRow = {
  character_id: string | null;
  wcl_name: string;
  heat_points: number;
  character_name: string | null;
  class_name: string | null;
  breakdown: {
    role?: string;
    zones?: { zone_id: number; zone_name: string | null; eligible: number; points: number }[];
    categories?: {
      key: string;
      label: string;
      used: number;
      eligible: number;
      casts: number;
      points: number;
      is_bonus: boolean;
      items?: string[];
      fights?: number[];
    }[];
  };
};

const CLASS_COLORS: Record<string, string> = {
  Warrior: "#C79C6E",
  Paladin: "#F58CBA",
  Hunter: "#ABD473",
  Rogue: "#FFF569",
  Priest: "#FFFFFF",
  Shaman: "#0070DE",
  Mage: "#69CCF0",
  Warlock: "#9482C9",
  Druid: "#FF7D0A",
};

function classColor(className: string | null): string {
  return (className && CLASS_COLORS[className]) || "#94a3b8";
}

type AnyDetailCategory = {
  key: string;
  label: string;
  is_bonus: boolean;
  used: number;
  eligible: number;
  points: number;
  items?: string[];
  fights?: (FightRef | number)[];
};

function resolveFight(f: FightRef | number, lookup?: Map<number, FightRef>): FightRef {
  if (typeof f === "number") return lookup?.get(f) || { id: f, name: `Fight ${f}`, kill: false };
  return f;
}

// Shared per-player detail: what consumables we picked up, per zone and per fight.
function ConsumableDetail({
  zones,
  categories,
  fightLookup,
}: {
  zones?: { zone_name: string | null; points: number; eligible: number }[];
  categories: AnyDetailCategory[];
  fightLookup?: Map<number, FightRef>;
}) {
  const active = categories.filter((c) => c.points > 0 || c.used > 0 || !c.is_bonus);
  return (
    <div className="space-y-2">
      {zones && zones.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {zones.map((z, i) => (
            <span key={i} className="rounded px-2 py-0.5 text-[10px] font-semibold border border-amber-500/25 bg-amber-500/[0.06] text-amber-800/90 dark:text-amber-100/90">
              {z.zone_name || "Unknown zone"}: +{Number(z.points).toFixed(1)} ({z.eligible} fights)
            </span>
          ))}
        </div>
      )}
      <table className="w-full text-left text-[10.5px]">
        <thead className="text-[var(--c-ink-3)] uppercase tracking-wider">
          <tr>
            <th className="px-2 py-1">Category</th>
            <th className="px-2 py-1">Detected items</th>
            <th className="px-2 py-1">Fights covered</th>
            <th className="px-2 py-1 text-right">Heat</th>
          </tr>
        </thead>
        <tbody>
          {active.map((c) => {
            const fights = (c.fights || []).map((f) => resolveFight(f, fightLookup));
            return (
              <tr key={c.key} className="border-t border-[var(--c-line-soft)] align-top">
                <td className="px-2 py-1 whitespace-nowrap text-[var(--c-ink-2)] font-semibold">
                  {c.label}
                  {c.is_bonus && <span className="ml-1 text-amber-700/70 dark:text-amber-300/70 normal-case">(bonus)</span>}
                </td>
                <td className="px-2 py-1 text-[var(--c-ink-3)]">
                  {c.items && c.items.length ? c.items.join(", ") : <span className="text-[var(--c-ink-4)]">none</span>}
                </td>
                <td className="px-2 py-1 text-[var(--c-ink-3)]">
                  {fights.length ? (
                    <span className="flex flex-wrap gap-1">
                      {fights.map((f) => (
                        <span
                          key={f.id}
                          className={`rounded px-1.5 py-0.5 text-[9px] border ${
                            f.kill ? "border-emerald-500/25 bg-emerald-500/[0.08] text-emerald-800/80 dark:text-emerald-200/80" : "border-[var(--c-line)] bg-[var(--c-bg-veil)] text-[var(--c-ink-3)]"
                          }`}
                        >
                          {f.name}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="text-[var(--c-ink-4)]">{c.used}/{c.eligible}</span>
                  )}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-amber-700/90 dark:text-amber-300/90 font-semibold">+{Number(c.points).toFixed(1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ImportDetailTable({ rows, fights }: { rows: DetailRow[]; fights: FightRef[] }) {
  const [open, setOpen] = React.useState<number | null>(null);
  const fightLookup = React.useMemo(() => new Map(fights.map((f) => [f.id, f])), [fights]);
  return (
    <table className="w-full text-left text-[11px]">
      <thead className="border-b border-[var(--c-line)] text-[var(--c-ink-3)] uppercase tracking-wider">
        <tr>
          <th className="px-2 py-1.5">Player</th>
          <th className="px-2 py-1.5">Breakdown</th>
          <th className="px-2 py-1.5 text-right">Heat</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d, i) => {
          const cats = d.breakdown?.categories || [];
          const expanded = open === i;
          return (
            <React.Fragment key={i}>
              <tr className="border-b border-[var(--c-line-soft)] last:border-0">
                <td className="px-2 py-1.5 font-semibold whitespace-nowrap" style={{ color: tc(classColor(d.class_name)) }}>
                  {cats.length ? (
                    <button type="button" onClick={() => setOpen(expanded ? null : i)} className="hover:underline" title="Show what we picked up">
                      <span className="text-[var(--c-ink-4)] mr-1">{expanded ? "▾" : "▸"}</span>
                      {d.character_name || d.wcl_name}
                    </button>
                  ) : (
                    d.character_name || d.wcl_name
                  )}
                </td>
                <td className="px-2 py-1.5 text-[var(--c-ink-3)]">
                  {cats
                    .filter((c) => c.points > 0 || !c.is_bonus)
                    .map((c) => `${c.label} ${c.used}/${c.eligible} (+${c.points.toFixed(1)})`)
                    .join(" · ") || "No tracked usage"}
                </td>
                <td className="px-2 py-1.5 text-right font-bold tabular-nums text-amber-700/95 dark:text-amber-300/95">+{Number(d.heat_points).toFixed(1)}</td>
              </tr>
              {expanded && (
                <tr className="bg-[var(--c-surface)]">
                  <td colSpan={3} className="px-4 py-3">
                    <ConsumableDetail zones={d.breakdown?.zones} categories={cats as AnyDetailCategory[]} fightLookup={fightLookup} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

const WARNING_COPY: Record<string, string> = {
  no_eligible_fights: "No scoreable boss fights in this report. Nothing can be committed.",
  low_roster_overlap: "Less than half the players in this report match your roster. Double check this is the right log for this guild.",
  other_import_same_night: "Another active import (a different report) already covers this night. Committing could reward the same players twice.",
  will_overwrite: "This report was already imported. Committing replaces the previous import: its old heat is removed and these new values take its place.",
};

export default function GuildConsumablesTab({ guildId, members, gameId = null }: Props) {
  const [config, setConfig] = useState<{ categories: ConfigCategory[]; max_per_import: number; bonus_max: number } | null>(null);
  const [reportUrl, setReportUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [previewId, setPreviewId] = useState<string | null>(null);
  const [report, setReport] = useState<PreviewReport | null>(null);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [rosterOverlap, setRosterOverlap] = useState<number>(100);
  const [hiddenNoUsage, setHiddenNoUsage] = useState<number>(0);
  const [canCommit, setCanCommit] = useState(false);
  const [rowCharacter, setRowCharacter] = useState<Record<number, string>>({});
  const [rowSkip, setRowSkip] = useState<Record<number, boolean>>({});
  const [openRow, setOpenRow] = useState<number | null>(null);

  const [imports, setImports] = useState<ImportRow[]>([]);
  const [openImport, setOpenImport] = useState<string | null>(null);
  const [detailRows, setDetailRows] = useState<Record<string, DetailRow[]>>({});
  const [detailFights, setDetailFights] = useState<Record<string, FightRef[]>>({});
  const [showHow, setShowHow] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const q = gameId ? `?gameId=${encodeURIComponent(gameId)}` : "";
      const res = await fetch(`${API_URL}/guilds/${guildId}/consumables/config${q}`, { credentials: "include" });
      if (!res.ok) return;
      setConfig(await res.json());
    } catch {
      /* ignore */
    }
  }, [guildId, gameId]);

  const loadImports = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/guilds/${guildId}/consumables/imports`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setImports(Array.isArray(data.imports) ? data.imports : []);
    } catch {
      /* ignore */
    }
  }, [guildId]);

  useEffect(() => {
    loadConfig();
    loadImports();
  }, [loadConfig, loadImports]);

  const runPreview = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/guilds/${guildId}/consumables/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reportUrl, gameId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msgs: Record<string, string> = {
          invalid_report_url: "That does not look like a Warcraft Logs report URL. Expected something like warcraftlogs.com/reports/AbC123...",
          report_not_found: "WCL could not find that report. Is it private?",
          report_already_imported: "This report was already imported. Re-uploading now will replace the previous import.",
          wcl_not_configured: "WCL API keys are not configured on the server.",
          wcl_rate_limited: "Warcraft Logs is rate limiting requests right now. Wait a minute and try again.",
          wcl_api_error: "Warcraft Logs is having server problems right now (their side, not ours). Wait a few minutes and try again.",
        };
        throw new Error(msgs[data.error] || data.error || `HTTP ${res.status}`);
      }
      setPreviewId(data.previewId);
      setReport(data.report);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setRosterOverlap(typeof data.roster_overlap === "number" ? data.roster_overlap : 100);
      setHiddenNoUsage(typeof data.hidden_no_usage === "number" ? data.hidden_no_usage : 0);
      setCanCommit(!!data.canCommit);
      setRowCharacter({});
      const skip: Record<number, boolean> = {};
      for (const r of data.rows || []) {
        if (r.match_status === "unmatched" || r.match_status === "ambiguous") skip[r.actor_id] = true;
      }
      setRowSkip(skip);
    } catch (e: any) {
      setPreviewId(null);
      setReport(null);
      setRows([]);
      setError(e?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!previewId) return;
    setError(null);
    setSuccess(null);
    setCommitting(true);
    try {
      const rowActions = rows.map((r) => {
        if (rowSkip[r.actor_id]) return { actorId: r.actor_id, skip: true };
        const fix = rowCharacter[r.actor_id];
        if (fix && (r.match_status === "unmatched" || r.match_status === "ambiguous")) {
          return { actorId: r.actor_id, characterId: fix };
        }
        return { actorId: r.actor_id };
      });
      const res = await fetch(`${API_URL}/guilds/${guildId}/consumables/import/commit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ previewId, rowActions }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccess(`Awarded heat to ${data.imported} player(s), ${Number(data.total_heat).toFixed(1)} heat total.`);
      setPreviewId(null);
      setReport(null);
      setRows([]);
      setReportUrl("");
      await loadImports();
    } catch (e: any) {
      setError(e?.message || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const revoke = async (imp: ImportRow) => {
    const note = window.prompt(
      `Revoke this import (${imp.report_title || imp.report_code})? All heat it granted is removed on the next recalculation. Optional note:`
    );
    if (note === null) return;
    const res = await fetch(`${API_URL}/guilds/${guildId}/consumables/imports/${imp.id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ note }),
    });
    if (res.ok) {
      await loadImports();
      setDetailRows((prev) => {
        const next = { ...prev };
        delete next[imp.id];
        return next;
      });
    }
  };

  const toggleDetail = async (importId: string) => {
    if (openImport === importId) {
      setOpenImport(null);
      return;
    }
    setOpenImport(importId);
    if (!detailRows[importId]) {
      try {
        const res = await fetch(`${API_URL}/guilds/${guildId}/consumables/imports/${importId}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setDetailRows((prev) => ({ ...prev, [importId]: Array.isArray(data.rows) ? data.rows : [] }));
          setDetailFights((prev) => ({ ...prev, [importId]: Array.isArray(data.fights) ? data.fights : [] }));
        }
      } catch {
        /* ignore */
      }
    }
  };

  const categoryColumns = useMemo(() => {
    if (rows.length > 0) return rows[0].categories.map((c) => ({ key: c.key, label: c.label, is_bonus: c.is_bonus }));
    return (config?.categories || []).map((c) => ({ key: c.key, label: c.label, is_bonus: c.is_bonus }));
  }, [rows, config]);

  const commitStats = useMemo(() => {
    let included = 0;
    let skipped = 0;
    let needFix = 0;
    let heat = 0;
    for (const r of rows) {
      if (rowSkip[r.actor_id]) {
        skipped++;
        continue;
      }
      if ((r.match_status === "unmatched" || r.match_status === "ambiguous") && !rowCharacter[r.actor_id]) {
        needFix++;
        continue;
      }
      included++;
      if (r.proposed_heat != null) heat += r.proposed_heat;
    }
    return { included, skipped, needFix, heat };
  }, [rows, rowSkip, rowCharacter]);

  const maxHeat = config?.max_per_import ?? 5;

  return (
    <div className="space-y-6 animate-in fade-in duration-200 max-w-6xl">
      <div>
        <h3 className="text-sm font-bold text-[var(--c-ink)] uppercase tracking-widest mb-1">Consumable heat</h3>
        <p className="text-xs text-[var(--c-ink-3)] leading-relaxed max-w-3xl">
          Paste a Warcraft Logs report URL from your raid night. Players earn heat for using tracked consumables
          (potions, runes, tank prep, engineering) on each boss fight. Review the preview, fix any unmatched names,
          then commit to award heat.{" "}
          <button type="button" onClick={() => setShowHow((v) => !v)} className="text-amber-700/90 dark:text-amber-300/90 hover:text-amber-800 dark:hover:text-amber-200 underline underline-offset-2">
            {showHow ? "Hide scoring rules" : "How scoring works"}
          </button>
        </p>
      </div>

      {showHow && config && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 space-y-3 text-xs text-[var(--c-ink-2)] leading-relaxed">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800/90 dark:text-amber-200/90">Scoring rules</div>
          <ul className="list-disc pl-4 space-y-1.5">
            <li>
              Heat is based on <span className="text-[var(--c-ink)] font-semibold">coverage, not totals</span>: using at least one tracked item
              from a category on a boss fight covers that fight. Chugging 5 potions on one boss counts the same as 1.
            </li>
            <li>
              Only boss fights count (kills, plus wipes longer than 45 seconds). Trash and instant resets are ignored.
            </li>
            <li>
              Each player is scored only on categories that fit their role, averaged so every role can reach the same max:{" "}
              <span className="text-[var(--c-ink)] font-semibold">up to +{maxHeat} heat per zone (raid)</span>, half what attendance is worth.
              Linked raids count separately: a Gruul + Magtheridon night scores each zone on its own and sums, up to +{maxHeat * 2}.
            </li>
            <li>
              Bonus items (engineering, drums, Nightmare Seed) add up to +{config.bonus_max} inside the cap. Using a category outside
              your role (like runes on a feral) also counts as bonus. None of it ever lowers a score.
            </li>
            <li>Using extra consumables never reduces heat. There is no penalty, only missed coverage.</li>
          </ul>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-1">
            {config.categories.map((c) => (
              <div key={c.key} className="rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] mb-1">
                  {c.label}
                  {c.is_bonus && <span className="ml-1 text-amber-700/80 dark:text-amber-300/80 normal-case">(bonus)</span>}
                </div>
                <div className="text-[10px] text-[var(--c-ink-3)] leading-relaxed">{c.spells.map((s) => s.label).join(", ")}</div>
                <div className="text-[9px] text-[var(--c-ink-4)] mt-1 uppercase tracking-wide">
                  {c.is_bonus ? "everyone" : c.applies_to.map((r) => r.replace("_dps", " dps")).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={reportUrl}
            onChange={(e) => setReportUrl(e.target.value)}
            placeholder="https://classic.warcraftlogs.com/reports/..."
            className="flex-1 min-w-[280px] rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] px-3 py-2 text-xs text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] font-mono focus:outline-none focus:border-amber-500/40"
          />
          <button
            type="button"
            disabled={loading || !reportUrl.trim()}
            onClick={runPreview}
            className="rounded-lg bg-amber-500/20 border border-amber-500/35 px-4 py-2 text-xs font-bold text-amber-800 dark:text-amber-100 hover:bg-amber-500/30 disabled:opacity-40"
          >
            {loading ? "Fetching from WCL…" : "Fetch report"}
          </button>
          {rows.length > 0 && (
            <button
              type="button"
              disabled={committing || !canCommit || commitStats.included === 0}
              onClick={commit}
              className="rounded-lg bg-emerald-500/20 border border-emerald-500/35 px-4 py-2 text-xs font-bold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40"
            >
              {committing ? "Awarding…" : `Commit (+${commitStats.heat.toFixed(1)} heat to ${commitStats.included})`}
            </button>
          )}
        </div>
        {error && <div className="text-xs text-red-600/95 dark:text-red-400/95">{error}</div>}
        {success && <div className="text-xs text-emerald-700/95 dark:text-emerald-400/95">{success}</div>}
      </div>

      {report && (
        <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.04] p-4 space-y-2">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-sm font-bold text-[var(--c-ink)]">{report.title}</span>
            <span className="text-xs text-[var(--c-ink-3)]">{report.zone || "Unknown zone"}</span>
            <span className="text-xs text-[var(--c-ink-3)]">{new Date(report.starts_at).toLocaleDateString()}</span>
            <span className="text-xs text-sky-800/80 dark:text-sky-200/80 font-semibold tabular-nums">
              {report.fights_eligible} scoreable boss fight(s)
            </span>
            {report.zones && report.zones.length > 1 && (
              <span className="text-xs text-amber-800/90 dark:text-amber-200/90 font-semibold tabular-nums">
                {report.zones.length} zones · up to +{report.max_heat ?? maxHeat} heat
              </span>
            )}
            <span className="text-xs text-[var(--c-ink-3)] tabular-nums">roster match {rosterOverlap}%</span>
          </div>
          {report.zones && report.zones.length > 1 && (
            <div className="text-[11px] text-[var(--c-ink-3)]">
              Linked night: each zone is scored separately (up to +{maxHeat} each) and summed.{" "}
              {report.zones.map((z) => `${z.zone_name || "Unknown"} (${z.fights})`).join(", ")}
            </div>
          )}
          {report.fights.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {report.fights.map((f) => (
                <span
                  key={f.id}
                  className={`rounded px-2 py-0.5 text-[10px] font-semibold border ${
                    f.kill
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-800/90 dark:text-emerald-200/90"
                      : "border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-3)]"
                  }`}
                >
                  {f.name}
                  {!f.kill && " (wipe)"}
                </span>
              ))}
            </div>
          )}
          {warnings.map((w) => (
            <div key={w} className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-800/90 dark:text-amber-100/90">
              {WARNING_COPY[w] || w}
            </div>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-xl border border-[var(--c-line)] overflow-hidden">
          <div className="px-4 py-2 border-b border-[var(--c-line-soft)] bg-[var(--c-surface)] flex justify-between items-center">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">Preview · fights covered per category</span>
            <span className="text-[10px] text-[var(--c-ink-4)]">
              {commitStats.included} ready · {commitStats.needFix} need a name fix · {commitStats.skipped} skipped
            </span>
          </div>
          <div className="overflow-x-auto max-h-[min(560px,60vh)] overflow-y-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="sticky top-0 bg-[var(--c-surface)] z-10 border-b border-[var(--c-line)]">
                <tr className="text-[var(--c-ink-3)] uppercase tracking-wider">
                  <th className="px-2 py-2">Player</th>
                  <th className="px-2 py-2">Match</th>
                  {categoryColumns.map((c) => (
                    <th key={c.key} className="px-2 py-2 text-center whitespace-nowrap">
                      {c.label}
                      {c.is_bonus && <span className="text-amber-700/70 dark:text-amber-300/70 normal-case"> +</span>}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-right">Heat</th>
                  <th className="px-2 py-2">Fix</th>
                  <th className="px-2 py-2">Skip</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const needsFix = r.match_status === "unmatched" || r.match_status === "ambiguous";
                  const skipped = !!rowSkip[r.actor_id];
                  const expanded = openRow === r.actor_id;
                  const hasDetail = !!r.detail && r.detail.categories.length > 0;
                  return (
                    <React.Fragment key={r.actor_id}>
                    <tr className={`border-b border-[var(--c-line-soft)] hover:bg-[var(--c-bg-veil)] ${skipped ? "opacity-40" : ""}`}>
                      <td className="px-2 py-1.5 font-semibold whitespace-nowrap" style={{ color: tc(classColor(r.wcl_class)) }}>
                        {hasDetail ? (
                          <button
                            type="button"
                            onClick={() => setOpenRow(expanded ? null : r.actor_id)}
                            className="hover:underline"
                            title="Show what we picked up"
                          >
                            <span className="text-[var(--c-ink-4)] mr-1">{expanded ? "▾" : "▸"}</span>
                            {r.wcl_name}
                          </button>
                        ) : (
                          r.wcl_name
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {r.match_status === "matched" || r.match_status === "mapped" ? (
                          <span className="text-emerald-700/90 dark:text-emerald-400/90">{r.character_name}</span>
                        ) : (
                          <span className="text-rose-600/90 dark:text-rose-400/90">{r.match_status}</span>
                        )}
                      </td>
                      {r.categories.map((c) => {
                        const inactive = c.applies === false;
                        const full = c.eligible > 0 && c.used >= c.eligible;
                        return (
                          <td
                            key={c.key}
                            className={`px-2 py-1.5 text-center tabular-nums ${
                              inactive
                                ? c.used > 0 ? "text-amber-700/70 dark:text-amber-300/70" : "text-[var(--c-ink-4)]"
                                : c.used === 0 ? "text-[var(--c-ink-4)]" : full ? "text-emerald-700/95 dark:text-emerald-300/95 font-semibold" : "text-[var(--c-ink-2)]"
                            }`}
                            title={
                              inactive
                                ? c.used > 0
                                  ? "Off-role usage: counts toward bonus"
                                  : "Does not apply to this role"
                                : `${c.casts} use(s) across ${c.used} fight(s)`
                            }
                          >
                            {inactive && c.used === 0 ? "·" : `${c.used}/${c.eligible}`}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 text-right font-bold tabular-nums text-amber-700/95 dark:text-amber-300/95">
                        {r.proposed_heat != null && !skipped ? `+${r.proposed_heat.toFixed(1)}` : "·"}
                      </td>
                      <td className="px-2 py-1.5">
                        {needsFix && (
                          <select
                            value={rowCharacter[r.actor_id] || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setRowCharacter((prev) => ({ ...prev, [r.actor_id]: v }));
                              if (v) setRowSkip((prev) => ({ ...prev, [r.actor_id]: false }));
                            }}
                            className="max-w-[150px] rounded border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] text-[10px] text-[var(--c-ink)] py-1"
                          >
                            <option value="">Not in guild</option>
                            {(r.match_candidates.length ? r.match_candidates : members).map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                            {r.match_candidates.length > 0 &&
                              members
                                .filter((m) => !r.match_candidates.some((c) => c.id === m.id))
                                .map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.name}
                                  </option>
                                ))}
                          </select>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={skipped}
                          onChange={(e) => setRowSkip((prev) => ({ ...prev, [r.actor_id]: e.target.checked }))}
                          className="accent-amber-500"
                        />
                      </td>
                    </tr>
                    {expanded && r.detail && (
                      <tr className="bg-[var(--c-surface)]">
                        <td colSpan={categoryColumns.length + 5} className="px-4 py-3">
                          <ConsumableDetail zones={r.detail.zones} categories={r.detail.categories as AnyDetailCategory[]} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-[10px] text-[var(--c-ink-3)] bg-[var(--c-surface)] border-t border-[var(--c-line-soft)]">
            {hiddenNoUsage > 0 && (
              <span className="text-[var(--c-ink-3)] font-semibold">
                {hiddenNoUsage} non-guild player(s) with no tracked usage hidden.{" "}
              </span>
            )}
            Unmatched players (pugs, cross-guild) are skipped by default. Assign a character to include them; the mapping is
            remembered for future imports. Heat recalculates from the assigned character's role at commit.
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[var(--c-line-soft)] overflow-hidden">
        <div className="px-4 py-2 border-b border-[var(--c-line-soft)] bg-[var(--c-surface)]">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">Import log</span>
        </div>
        {imports.length === 0 ? (
          <div className="px-4 py-6 text-xs text-[var(--c-ink-4)]">No consumable imports yet. Every commit and revoke is recorded here.</div>
        ) : (
          <ul>
            {imports.map((imp) => (
              <li key={imp.id} className="border-b border-[var(--c-line-soft)] last:border-0">
                <div className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <button
                    type="button"
                    onClick={() => toggleDetail(imp.id)}
                    className="text-xs font-semibold text-[var(--c-ink)] hover:text-amber-800 dark:hover:text-amber-200"
                  >
                    {openImport === imp.id ? "▾" : "▸"} {imp.report_title || imp.report_code}
                  </button>
                  <span className="text-[11px] text-[var(--c-ink-3)]">{imp.zone_name || ""}</span>
                  <span className="text-[11px] text-[var(--c-ink-3)]">{new Date(imp.report_start).toLocaleDateString()}</span>
                  <span className="text-[11px] text-[var(--c-ink-3)] tabular-nums">
                    {imp.players} player(s) · +{Number(imp.total_heat).toFixed(1)} heat · {imp.fights_eligible} fight(s)
                  </span>
                  {imp.imported_by_name && <span className="text-[10px] text-[var(--c-ink-4)]">by {imp.imported_by_name}</span>}
                  {imp.revoked_at ? (
                    <span
                      className="rounded px-2 py-0.5 text-[10px] font-bold border border-rose-500/30 bg-rose-500/10 text-rose-800/90 dark:text-rose-200/90"
                      title={`${imp.revoked_by_name ? `Revoked by ${imp.revoked_by_name}. ` : ""}${imp.revoke_note || ""}`}
                    >
                      REVOKED
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => revoke(imp)}
                      className="ml-auto text-[10px] text-red-600/70 dark:text-red-400/70 hover:text-red-700 dark:hover:text-red-300"
                    >
                      Revoke
                    </button>
                  )}
                  <a
                    href={imp.report_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] text-sky-700/80 dark:text-sky-300/80 hover:underline"
                  >
                    WCL ↗
                  </a>
                </div>
                {openImport === imp.id && (
                  <div className="px-4 pb-3">
                    {!detailRows[imp.id] ? (
                      <div className="text-[11px] text-[var(--c-ink-4)] py-2">Loading…</div>
                    ) : (
                      <ImportDetailTable rows={detailRows[imp.id]} fights={detailFights[imp.id] || []} />
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
